/**
 * The viewer's open-and-close, for any screen with something to look at.
 *
 * The spotlight is a generic overlay — photographs on the work record,
 * documents on a chain of title — and this hook is the part that is the same
 * for all of them: it holds whether the viewer is shut, resolving or open, and
 * nothing about where the bytes came from. The caller hands in a loader, and
 * `spotlight.model.ts` decides how each loaded source can be shown.
 *
 * No bucket name and no storage client reaches this module. That is what the
 * loader-as-thunk buys, and it is also why nothing is fetched or signed until
 * somebody has actually asked to look.
 */

import { useCallback, useRef, useState } from "react";

import { toSpotlightItems } from "./spotlight.model.ts";
import type { SpotlightItem, SpotlightSource } from "./spotlight.model.ts";

/**
 * What a caller does to get hold of the bytes.
 *
 * A thunk rather than data, so nothing is fetched or signed until somebody has
 * actually asked to look — and a thunk rather than a resolver argument on the
 * hook, so no bucket name and no storage client ever reaches this module.
 *
 * `release` runs on close. A caller that made object URLs revokes them there.
 */
export type SpotlightLoader = () => Promise<{
  sources: SpotlightSource[];
  release?: () => void;
}>;

export type SpotlightView =
  | { kind: "shut" }
  | { kind: "resolving" }
  | { kind: "open"; items: SpotlightItem[]; index: number };

/**
 * Opening and closing the viewer.
 *
 * Nothing is shown until the loader has settled, so a slide never appears with
 * a URL still on the way — `resolving` is what the viewer shows instead.
 *
 * A load that fails leaves the viewer shut rather than showing an empty
 * overlay: whatever went wrong belongs to the screen underneath, which still
 * has its own error surface.
 */
export function useSpotlight() {
  const [view, setView] = useState<SpotlightView>({ kind: "shut" });
  /** Not rendered, and must survive a re-render, so a ref rather than state. */
  const release = useRef<(() => void) | null>(null);
  /** Which open this is. A slow load whose viewer was closed must not reopen it. */
  const generation = useRef(0);

  const close = useCallback(() => {
    generation.current += 1;
    release.current?.();
    release.current = null;
    setView({ kind: "shut" });
  }, []);

  const open = useCallback(async (load: SpotlightLoader, index: number) => {
    generation.current += 1;
    const mine = generation.current;
    release.current?.();
    release.current = null;
    setView({ kind: "resolving" });

    try {
      const loaded = await load();
      if (generation.current !== mine) {
        loaded.release?.();
        return;
      }

      release.current = loaded.release ?? null;
      setView({ kind: "open", items: toSpotlightItems(loaded.sources), index });
    } catch {
      if (generation.current === mine) setView({ kind: "shut" });
    }
  }, []);

  return { view, open, close };
}
